import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  LinearProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import locationService from '../../services/locationService';
import Layout from '../../components/layout/Layout';
import PageTitle from '../../components/PageTitle';
import { useSnackbar } from 'notistack';
import { downloadXlsxFromRows } from '../../utils/inventoryListExport';

const defaultForm = {
  name: '',
  address: '',
  city: '',
  phone: '',
  email: '',
  status: 'ACTIVE',
  enableWhatsappShare: false,
};

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    py: 0.65,
    px: 1.5,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiTableCell-head': {
    py: 0.85,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    bgcolor: 'action.hover',
  },
};

const LocationsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const nameInputRef = useRef(null);
  const [allRows, setAllRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitDialogMessage, setLimitDialogMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await locationService.list({ page: 0, size: 2000, sort: 'name', direction: 'ASC' });
      setAllRows(res?.content || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setAllRows([]);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load locations';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    if (!normalizedSearch) return allRows;
    return allRows.filter((row) => {
      const hay = [row.name, row.address, row.city, row.phone, row.email, row.status, String(row.id ?? '')]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(normalizedSearch);
    });
  }, [allRows, normalizedSearch]);

  const displayedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handleExportLocationsExcel = useCallback(() => {
    const rows = filteredRows.map((row, idx) => ({
      'S.No': idx + 1,
      Name: row.name || '',
      Address: row.address || '',
      City: row.city || '',
      Phone: row.phone || '',
      Email: row.email || '',
      Status: row.status || '',
    }));
    const ok = downloadXlsxFromRows(
      `locations_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Locations',
      rows
    );
    if (!ok) enqueueSnackbar('Nothing to export', { variant: 'info' });
  }, [filteredRows, enqueueSnackbar]);

  const handleChangePage = (e, newPage) => {
    const maxPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
    setPage(Math.min(newPage, maxPage));
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    if (!row || !row.id) {
      console.error('Invalid row data for editing:', row);
      enqueueSnackbar('Invalid location data', { variant: 'error' });
      return;
    }
    setEditing(row);
    setForm({
      name: row.name || '',
      address: row.address || '',
      city: row.city || '',
      phone: row.phone || '',
      email: row.email || '',
      status: row.status || 'ACTIVE',
      enableWhatsappShare: Boolean(row.enableWhatsappShare),
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const errors = {};
    if (!form.name.trim()) {
      errors.name = 'Location name is required';
    }
    if (form.phone) {
      const digits = String(form.phone).replace(/\D/g, '').slice(0, 15);
      if (digits.length === 0) {
        errors.phone = 'Phone must contain digits';
      } else if (digits.length > 15) {
        errors.phone = 'Phone must be at most 15 digits';
      }
    }
    if (form.email) {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (!pattern.test(form.email)) {
        errors.email = 'Invalid email format';
      }
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const payload = { ...form };
    try {
      if (editing) {
        await locationService.update(editing.id, payload);
        enqueueSnackbar('Location updated successfully', { variant: 'success' });
      } else {
        await locationService.create(payload);
        enqueueSnackbar('Location created successfully', { variant: 'success' });
      }
      setDialogOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving location:', error);
      const msg = error.response?.data?.message || 'Failed to save location';
      if (/limit|exceed|maximum|reached/i.test(String(msg))) {
        setLimitDialogMessage(String(msg));
        setLimitDialogOpen(true);
      } else {
        enqueueSnackbar(msg, { variant: 'error' });
      }
    }
  };

  const handleDelete = async (row) => {
    if (!row || !row.id) {
      console.error('Invalid row data for deletion:', row);
      enqueueSnackbar('Invalid location data', { variant: 'error' });
      return;
    }
    if (window.confirm(`Delete location "${row.name || 'this location'}"?`)) {
      try {
        await locationService.remove(row.id);
        enqueueSnackbar('Location deleted successfully', { variant: 'success' });
        await fetchData();
      } catch (error) {
        console.error('Error deleting location:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete location';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'phone') {
      v = value.replace(/\D/g, '').slice(0, 15);
    }
    setForm((prev) => ({ ...prev, [name]: v }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (name === 'email') {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (v && !pattern.test(v)) {
        setFormErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
      }
    }
  };

  return (
    <Layout>
      <PageTitle title="Locations" />
      <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2, md: 3 }, py: 1.5, bgcolor: '#fafbfc', minHeight: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Locations
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search name, address, city, phone, email…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 280 }, flex: { sm: 1 } }}
            />
            <Button variant="outlined" color="primary" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportLocationsExcel}>
              Export Excel
            </Button>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={openCreate}>
              New Location
            </Button>
          </Stack>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '95%', mx: 'auto' }}>
          <Paper variant="outlined" elevation={0} sx={{ borderRadius: 1, overflow: 'hidden' }}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell width="18%">Name</TableCell>
                    <TableCell width="22%">Address</TableCell>
                    <TableCell width="10%">City</TableCell>
                    <TableCell width="12%">Phone</TableCell>
                    <TableCell width="18%">Email</TableCell>
                    <TableCell width="10%">Status</TableCell>
                    <TableCell width="10%" align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && allRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : displayedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        No locations
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap title={row.name}>
                            {row.name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={row.address || ''}>
                            {row.address?.trim() ? row.address : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {row.city || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {row.phone || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={row.email || ''}>
                            {row.email || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status || 'UNKNOWN'}
                            color={row.status === 'ACTIVE' ? 'success' : 'default'}
                            size="small"
                            sx={{ height: 24, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)} disabled={!row.id}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row)} disabled={!row.id}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              size="small"
            />
          </Paper>
        </Box>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          TransitionProps={{
            onEntered: () => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  nameInputRef.current?.focus({ preventScroll: true });
                });
              });
            },
          }}
        >
          <DialogTitle>{editing ? 'Edit Location' : 'New Location'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  inputRef={nameInputRef}
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  autoComplete="off"
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Address" name="address" value={form.address} onChange={handleChange} fullWidth size="small" multiline minRows={2} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="City" name="city" value={form.city} onChange={handleChange} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Status" name="status" value={form.status} onChange={handleChange} fullWidth size="small">
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.enableWhatsappShare)}
                      onChange={(e) => setForm((prev) => ({ ...prev, enableWhatsappShare: e.target.checked }))}
                      name="enableWhatsappShare"
                      color="primary"
                    />
                  }
                  label="Enable WhatsApp share"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Limit Reached</DialogTitle>
          <DialogContent>
            <Typography>{limitDialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLimitDialogOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default LocationsPage;
